import React, { useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceIcon } from "assets/material-custom-icons";
import { usePrompt } from "hooks";
import { useDealActions } from "store";
import { IFolder } from "store/types";

import { Create } from "../Create";
import { Folder } from "./Folder";

interface Props {
  dealId?: number;
  title?: string;
  folders: IFolder[];
  selectedFolderId: number | null;
  onSubmit: (folder: IFolder) => void;
  onCancel: () => void;
  onFolderCreate: (folder: IFolder) => void;
}

export const SelectDealFolder: React.FC<Props> = ({
  dealId,
  title = "Select a Folder",
  folders,
  onFolderCreate,
  selectedFolderId,
  onSubmit,
  onCancel
}) => {
  const { createFolderAction } = useDealActions();
  const openPrompt = usePrompt();

  const [selectedFolder, setSelectedFolder] = useState<IFolder | null>(null);
  const [foldersLocalState, setFoldersLocalState] = useState<IFolder[]>(folders);

  const handleSelectFolder = (folder: IFolder) => {
    setSelectedFolder(folder);
  };

  const handleCreateFolder = async () => {
    const title = await openPrompt({
      Component: Create,
      props: {
        title: "Create New Folder",
        label: "Folder name",
        submitButtonTitle: "Create"
      }
    });
    if (!title) return;
    const newFolder = await createFolderAction({ dealId, title: title, parent_id: selectedFolder?.id });
    setFoldersLocalState((prev) => [...(prev || []), newFolder]);
    setSelectedFolder(newFolder);
    onFolderCreate(newFolder);
  };

  const parentFolders = useMemo(() => {
    return foldersLocalState.filter((folder) => !folder.parent_id);
  }, [foldersLocalState]);

  const handleSubmit = () => {
    if (selectedFolder) {
      onSubmit(selectedFolder);
    }
  };

  return (
    <Stack
      sx={{
        padding: "32px",
        borderRadius: "8px",
        backgroundColor: "#fff",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "650px"
      }}
      gap="20px"
      justifyContent="space-between"
    >
      <Typography variant="h6" component="div" sx={{ display: "flex", justifyContent: "space-between" }}>
        {title}
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Typography>

      <Stack>
        <Stack
          sx={{
            border: "1px solid  #DBE8F5",
            // padding: "10px",
            height: "250px",
            overflow: "scroll",
            marginBottom: "10px"
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ height: "40px", borderBottom: "1px solid  #DAE0E2", maxWidth: "450px", width: "100%" }}
          >
            <Stack direction="row" alignItems="center" gap="10px" sx={{ paddingLeft: "10px" }}>
              <WorkspaceIcon sx={{ fill: "#0A3142" }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#0a3142",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden"
                }}
              >
                Workspace
              </Typography>
            </Stack>
          </Stack>
          {parentFolders.map((folder) => (
            <Folder
              key={folder.id}
              folder={folder}
              selectedFolderId={selectedFolder?.id || selectedFolderId}
              onSelectFolder={handleSelectFolder}
              folders={foldersLocalState}
            />
          ))}
        </Stack>
        <Button
          variant="text"
          sx={{ color: "#26A6AE", cursor: "pointer", width: "fit-content" }}
          onClick={handleCreateFolder}
          data-create-folder
        >
          Add Folder
        </Button>
      </Stack>

      <Stack flexDirection="row" alignItems="center" justifyContent="flex-end" gap="10px">
        <Button variant="outlined" sx={{ minWidth: "53px" }} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!selectedFolder}
          variant="contained"
          sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
          onClick={handleSubmit}
        >
          Select
        </Button>
      </Stack>
    </Stack>
  );
};
